/**
 * Reads constant data out of a page's script without running it.
 *
 * An imported page's script is someone else's code: executed in DiSI it would
 * act with the reader's session. What an importer needs from it is data - the
 * `const N = {...}` of a Klickprozess - so this understands just the literal
 * part of JavaScript: strings ('', "", `` without ${}), numbers, true/false/
 * null, arrays, objects with plain or quoted keys, `+` between them and names
 * of constants read before. Anything else (a call, a function, an expression
 * it does not know) makes that one constant unreadable, not the import.
 */

const PUNCTUATION = new Set(['{', '}', '[', ']', '(', ')', ',', ':', '+', '-', ';', '=']);
const ESCAPES = { n: '\n', t: '\t', r: '\r', b: '\b', f: '\f', v: '\v', 0: '\0' };

class Unreadable extends Error {}

function tokenize(source, start) {
  const tokens = [];
  let i = start;
  let depth = 0;

  while (i < source.length) {
    const c = source[i];

    // A declaration also ends at a line break or a "," (`const A=1, B=2`)
    // outside brackets once its value is complete.
    const complete = tokens.length > 0 && !['+', '-', '=', ',', ':', '('].includes(tokens[tokens.length - 1].type);
    if (depth <= 0 && complete && (c === '\n' || c === ',')) break;

    if (/\s/.test(c)) { i += 1; continue; }
    if (source.startsWith('//', i)) { i = source.indexOf('\n', i); if (i < 0) break; continue; }
    if (source.startsWith('/*', i)) { i = source.indexOf('*/', i) + 2; if (i < 2) break; continue; }

    if (c === '"' || c === "'" || c === '`') {
      let value = '';
      i += 1;
      while (i < source.length && source[i] !== c) {
        if (c === '`' && source.startsWith('${', i)) throw new Unreadable('template expression');
        if (source[i] === '\\') {
          const next = source[i + 1];
          if (next === 'u' && source[i + 2] === '{') {
            const end = source.indexOf('}', i);
            value += String.fromCodePoint(parseInt(source.slice(i + 3, end), 16));
            i = end + 1;
          } else if (next === 'u') {
            value += String.fromCharCode(parseInt(source.slice(i + 2, i + 6), 16));
            i += 6;
          } else if (next === 'x') {
            value += String.fromCharCode(parseInt(source.slice(i + 2, i + 4), 16));
            i += 4;
          } else if (next === '\n') {
            i += 2;
          } else {
            value += ESCAPES[next] ?? next;
            i += 2;
          }
        } else {
          value += source[i];
          i += 1;
        }
      }
      tokens.push({ type: 'string', value });
      i += 1;
      continue;
    }

    const number = /^(?:0[xX][0-9a-fA-F]+|\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\.\d+)/.exec(source.slice(i, i + 40));
    if (number) {
      tokens.push({ type: 'number', value: Number(number[0]) });
      i += number[0].length;
      continue;
    }

    const name = /^[A-Za-z_$][\w$]*/.exec(source.slice(i, i + 200));
    if (name) {
      tokens.push({ type: 'name', value: name[0] });
      i += name[0].length;
      continue;
    }

    if (PUNCTUATION.has(c)) {
      if ('{[('.includes(c)) depth += 1;
      if ('}])'.includes(c)) depth -= 1;
      tokens.push({ type: c });
      i += 1;
      // The declaration ends at a ";" or a line break outside any brackets.
      if (c === ';' && depth <= 0) break;
      continue;
    }

    throw new Unreadable(`unexpected "${c}"`);
  }

  return tokens;
}

function parseExpression(tokens, scope) {
  let at = 0;
  const peek = () => tokens[at];
  const take = (type) => {
    const token = tokens[at];
    if (!token || (type && token.type !== type)) throw new Unreadable(`expected ${type}`);
    at += 1;
    return token;
  };

  function primary() {
    const token = take();
    switch (token.type) {
      case 'string':
      case 'number':
        return token.value;
      case '-':
        return -primary();
      case '(': {
        const value = sum();
        take(')');
        return value;
      }
      case '[': {
        const list = [];
        while (peek()?.type !== ']') {
          list.push(sum());
          if (peek()?.type === ',') take(',');
        }
        take(']');
        return list;
      }
      case '{': {
        const object = {};
        while (peek()?.type !== '}') {
          const key = take();
          if (!['name', 'string', 'number'].includes(key.type)) throw new Unreadable('object key');
          take(':');
          object[key.value] = sum();
          if (peek()?.type === ',') take(',');
        }
        take('}');
        return object;
      }
      case 'name':
        if (token.value === 'null') return null;
        if (token.value === 'true') return true;
        if (token.value === 'false') return false;
        if (token.value === 'undefined') return undefined;
        if (Object.prototype.hasOwnProperty.call(scope, token.value)) return scope[token.value];
        throw new Unreadable(`unknown name ${token.value}`);
      default:
        throw new Unreadable(`unexpected ${token.type}`);
    }
  }

  function sum() {
    let value = primary();
    while (peek()?.type === '+') {
      take('+');
      value += primary();
    }
    return value;
  }

  const value = sum();
  if (at < tokens.length) {
    throw new Unreadable('trailing tokens');
  }
  return value;
}

/**
 * The top-level `const`/`let`/`var NAME = <literal>` declarations of
 * `source`, in order, each readable one as its value: `{ N: {...}, K: [...] }`.
 * A declaration may use the ones before it (`P + " more"`).
 */
export function readConstants(source) {
  const scope = {};
  const declaration = /(?:^|[;\n])\s*(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s*=(?!=)/g;
  let match;

  while ((match = declaration.exec(source))) {
    try {
      const tokens = tokenize(source, declaration.lastIndex);
      const end = tokens.findIndex((token) => token.type === ';');
      scope[match[1]] = parseExpression(end < 0 ? tokens : tokens.slice(0, end), scope);
    } catch (error) {
      if (!(error instanceof Unreadable)) throw error;
    }
  }

  return scope;
}
